class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.input?.form) {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    this.input?.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    // Header search drives the modal input; don't close results when focus
    // leaves the modal's predictive-search for the header field.
    if (this.hasAttribute('data-ub-modal-search')) return;

    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const sectionId = this.dataset.section || 'predictive-search';
    const isModal = this.hasAttribute('data-ub-modal-search');
    const queryKey = `${sectionId}::${searchTerm.replace(/\s+/g, '-').toLowerCase()}`;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Cancel any in-flight request so rapid typing doesn't race
    this.abortController.abort();
    this.abortController = new AbortController();

    const baseUrl = window.routes?.predictive_search_url || (typeof routes !== 'undefined' ? routes.predictive_search_url : '');
    if (!baseUrl) {
      console.error('Predictive search URL is not defined');
      this.removeAttribute('loading');
      return;
    }

    const buildUrl = (sid) => {
      const params = new URLSearchParams({
        q: searchTerm,
        section_id: sid,
      });

      if (isModal) {
        params.set('resources[type]', 'product');
        params.set('resources[limit]', '8');
      } else {
        params.set('resources[type]', 'query,product,collection,page,article');
        params.set('resources[limit_scope]', 'each');
      }

      return `${baseUrl}?${params.toString()}`;
    };

    const fetchSection = (sid) =>
      fetch(buildUrl(sid), {
        signal: this.abortController.signal,
        credentials: 'same-origin',
      });

    const loadSection = (sid, allowFallback) =>
      fetchSection(sid).then((response) => {
        if (!response.ok) {
          if (allowFallback && isModal && sid !== 'predictive-search') {
            return loadSection('predictive-search', false);
          }
          const error = new Error(String(response.status));
          error.status = response.status;
          throw error;
        }
        return response.text().then((text) => ({ text, sid }));
      });

    loadSection(sectionId, true)
      .then(({ text, sid }) => {
        const sectionEl = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector(`#shopify-section-${sid}`);

        if (!sectionEl) {
          this.removeAttribute('loading');
          console.error(`Predictive search section not found: ${sid}`);
          return;
        }

        const resultsMarkup = sectionEl.innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          const instanceSection = predictiveSearchInstance.dataset.section || 'predictive-search';
          if (instanceSection === sectionId || sid === 'predictive-search') {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') {
          return;
        }
        console.error('Predictive search failed:', error);
        this.removeAttribute('loading');
        if (!isModal) {
          this.close();
        }
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement?.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) {
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    }
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    // Ensure the Uncle Bill's modal shows the results panel
    const modal = this.closest('ub-search-modal');
    if (modal) {
      modal.classList.add('has-query');
      const featured = modal.querySelector('[data-ub-search-featured]');
      const resultsPanel = modal.querySelector('[data-ub-search-results-panel]');
      if (featured) featured.hidden = true;
      if (resultsPanel) resultsPanel.hidden = false;

      const isNoResults = this.predictiveSearchResults.querySelector('[data-ub-search-no-results]');
      if (isNoResults) {
        if (typeof modal.showNoResultsState === 'function') {
          modal.showNoResultsState(this.getQuery());
        }
      } else {
        modal.clearNoResultsState?.();
        if (typeof modal.injectFeaturedPageCard === 'function') {
          modal.injectFeaturedPageCard(this.getQuery());
        }
        if (typeof modal.bindResultsCarousels === 'function') {
          modal.bindResultsCarousels(this.predictiveSearchResults);
        }
      }

      window.PlpCardLayout?.scheduleUpdate?.(modal);
    }

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (this.hasAttribute('data-ub-modal-search')) {
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
      return;
    }

    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
